import { InputBase } from '@mantine/core';
import { CalendarIcon, IconChevronDown, IconChevronUp } from 'components/Icons';
import { Popover, PopoverAction } from 'components/Popover';
import { useCallback, useMemo } from 'react';
import { Formik, useFormikContext } from 'formik';
import { useModalActions } from 'components/ModalManager/Context';
import { useCompositePopover } from 'hooks/useCompositePopover';
import { noop } from 'utils/fp';
import { notificationModalStyles, notificationModalWrapperStyles } from 'components/Navbar/Navbar.styles';
import { object, array, date as yupDate } from 'yup';
import { DateRangeModal, DateRangePopover } from './Segments';
import {
  formatPeriod,
  DateRangePickerProps,
  DateRangeFormData,
  DEFAULT_SELECTED_DATE_RANGE,
  DateRange,
  toDateString,
  isBeforeSixMonths,
  isAfterToday,
} from './utils';
import { popoverAction } from './styles';

const DATE_RANGE_PICKER_MODAL_ID = 'date-range-picker-modal';

const validationSchema = object({
  range: array()
    .of(yupDate().typeError('Invalid date format'))
    .test('valid-date-range', 'From date must be within the last 6 months and To date cannot be in the future.', (value) => {
      if (!value || value.length !== 2) return false;

      const [fromDate, toDate] = value;
      const isFromDateValid = fromDate && isBeforeSixMonths(fromDate);
      const isToDateValid = toDate && isAfterToday(toDate);

      return !(isFromDateValid || isToDateValid);
    })
    .required('Date range is required'),
});

export function DateRangePickerInternal(props: Pick<DateRangePickerProps, 'value'> & { onChange: (value: DateRangeFormData) => void }) {
  const form = useFormikContext<DateRangeFormData>();

  const { openModal, closeModal } = useModalActions();
  const openDateRangePickerModal = useCallback(
    () =>
      openModal({
        id: DATE_RANGE_PICKER_MODAL_ID,
        title: '',
        makeActions: noop,
        closeOnBackdropClick: true,
        contentWrapperClassName: notificationModalStyles,
        className: notificationModalWrapperStyles,
        content: (
          <Formik
            onSubmit={async (values) => {
              props.onChange(values as DateRangeFormData);
            }}
            enableReinitialize
            {...form}
          >
            {({ resetForm }) => (
              <DateRangeModal
                closeAction={() => {
                  resetForm();
                  closeModal({ id: DATE_RANGE_PICKER_MODAL_ID });
                }}
              />
            )}
          </Formik>
        ),
      }),
    [form],
  );

  const { visible, popoverState, togglePopoverOrModal, closePopoverOrModal } = useCompositePopover({
    breakpointWidth: 800,
    modal: {
      id: DATE_RANGE_PICKER_MODAL_ID,
      open: openDateRangePickerModal,
      close: () => closeModal({ id: DATE_RANGE_PICKER_MODAL_ID }),
    },
  });

  const closeAction = useCallback(() => {
    closePopoverOrModal();
    form.resetForm();
  }, [form, closePopoverOrModal]);

  const DropdownIcon = useMemo(() => (visible ? IconChevronUp : IconChevronDown), [visible]);

  return (
    <>
      <PopoverAction popover={popoverState} className={popoverAction}>
        <InputBase
          w="max-content"
          component="div"
          size="md"
          icon={<CalendarIcon size={20} strokeWidth={1} />}
          rightSection={<DropdownIcon size={20} />}
          onClick={togglePopoverOrModal}
          data-testid="date-range-picker-clickable"
        >
          {formatPeriod(props.value[0])} - {formatPeriod(props.value[1])}
        </InputBase>
      </PopoverAction>
      <Popover popover={popoverState} className="no-padding" style={{ zIndex: 99 }}>
        <DateRangePopover closeAction={closeAction} />
      </Popover>
    </>
  );
}

export function DateRangePicker(props: DateRangePickerProps) {
  const onDateRangeChange = useCallback((values: DateRangeFormData) => {
    let range = values.range;
    const nonEmptyValues = values.range.filter(Boolean);

    // An unlikely edge case, just in case
    if (nonEmptyValues.length === 0) {
      range = DEFAULT_SELECTED_DATE_RANGE.range;
    }
    // Allowing user to select a single date: https://clarifai.atlassian.net/browse/MRK-4136
    else if (nonEmptyValues.length === 1) {
      range = [nonEmptyValues[0], nonEmptyValues[0]] as DateRange;
    }

    props.onChange(toDateString(range));
  }, []);

  return (
    <Formik<DateRangeFormData>
      initialValues={{
        range: toDateString(props.value || DEFAULT_SELECTED_DATE_RANGE.range),
      }}
      enableReinitialize
      onSubmit={onDateRangeChange}
      validationSchema={validationSchema}
    >
      {() => <DateRangePickerInternal {...props} value={props.value} onChange={onDateRangeChange} />}
    </Formik>
  );
}
